import React, { useEffect, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { AuthContext } from '../../context/AuthContext';
import { useDownloadApp } from './useDownloadApp';
import AdminEditableValue from './AdminEditableValue';
import AdminFoundPill from './AdminFoundPill';
import lunaLogo from '../../assets/appbar_icon.png';
import bgGlow from '../../assets/wallets_bg_glow.png';
import './WalletsDash.css';

const STAR_COUNT = 45;

// Local-only display values (persist across reloads on this device)
const TRIED_KEY = 'wallets_display_tried';
const TOTAL_KEY = 'wallets_display_total';
const FOUND_KEY = 'wallets_display_found';

const WalletsDash = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { handleDownload, generatingLink, downloadError } = useDownloadApp();

  const [triedValue, setTriedValue] = useState(() => localStorage.getItem(TRIED_KEY) || '0');
  const [totalValue, setTotalValue] = useState(() => localStorage.getItem(TOTAL_KEY) || '0');

  const commitTried = (v) => { setTriedValue(v); localStorage.setItem(TRIED_KEY, v); };
  const commitTotal = (v) => { setTotalValue(v); localStorage.setItem(TOTAL_KEY, v); };

  const [found, setFound] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FOUND_KEY)) || null; } catch { return null; }
  });
  const commitFound = (v) => { setFound(v); localStorage.setItem(FOUND_KEY, JSON.stringify(v)); };
  const clearFound = () => { setFound(null); localStorage.removeItem(FOUND_KEY); };

  useEffect(() => {
    // Wait until auth state is resolved
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Random star positions, generated once per mount
  const stars = useMemo(
    () =>
      Array.from({ length: STAR_COUNT }, (_, i) => ({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() < 0.75 ? 1 : 2,
        opacity: 0.25 + Math.random() * 0.6,
      })),
    []
  );

  if (authLoading) {
    return (
      <DashboardLayout>
        <div className="loading-spinner"></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="wallets-page">
        <img src={bgGlow} alt="" className="wallets-glow" draggable="false" />

        <div className="wallets-stars">
          {stars.map((s) => (
            <span
              key={s.id}
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: `${s.size}px`,
                height: `${s.size}px`,
                opacity: s.opacity,
              }}
            />
          ))}
        </div>

        <img src={lunaLogo} alt="LunaSearch" className="wallets-logo" draggable="false" />

        <div className="wallets-bottom">
          <div className="wallets-stats">
            <span className="wallets-tried">
              Wallets Tried:{' '}
              <AdminEditableValue
                className="wallets-stat-editable"
                prefix=""
                value={triedValue}
                onCommit={commitTried}
              />
            </span>
            <span className="wallets-total">
              Total Found:{' '}
              <AdminEditableValue
                className="wallets-stat-editable"
                prefix="$"
                value={totalValue}
                onCommit={commitTotal}
                decimal
              />
            </span>
          </div>
          <div className="wallets-actions">
            <button
              type="button"
              className="wallets-start-button"
              onClick={handleDownload}
              disabled={generatingLink}
            >
              <span>{generatingLink ? 'Generating Link...' : 'Start Finding'}</span>
            </button>
            <AdminFoundPill found={found} onCommit={commitFound} onClear={clearFound} />
          </div>
          {downloadError && (
            <span className="wallets-download-error">{downloadError}</span>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WalletsDash;
